/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'

import {
    Flex,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useMultiStyleConfig,
    Divider,
} from '@chakra-ui/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// project components
import Benefits from '../benefits'
import Breadcrumb from '../breadcrumb'
import ImageGallery, {Skeleton as ImageGallerySkeleton} from '../image-gallery'
import {InformativeLabelGroup} from '../informative-label'
import {BRAND_KEY} from '../../brand'

import ProductHeader from './product-header'
import ProductVariants from './product-variants'
import KlarnaWidget from '../klarna-widget'
import PaypalWidget from '../paypal-widget'
import EnergyLabel from '../../components/energy-label'
import {ADD_TO_CART_ERROR_MESSAGES} from '../../constants'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '../../hooks/use-toast'
import WhereToBuyModal from '../modals/whereToBuyModal'
import StockLabel from '../stock-label'
import {InfoIcon, CloseIcon, InfoLabelIcon, CloseLabelIcon} from '../icons'
import {getPriceData} from '../../utils/product-utils'
import PriceCard from '../price-card'
import {remCalc} from '../../theme/_utils'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {
    useCustomPreference,
    useBooleanCustomPreference,
    useCustomPreferenceWithFallback,
} from '../../hooks/use-custom-preference'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import NotifyMeButton from '../notify-me-button'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {isServer, parseAndSanitizeJSONWithSpaces} from '../../utils/utils'
import EditorialComponentsFactory from '../editorial-components/editorial-components-factory'
import useCorporateSale from '../../hooks/use-corporate-sale'
import {useMaxQuantityItems} from '../../hooks/use-max-quantity-items'
import ProductDetailBannerBlock from '../product-detail-banner/product-detail-banner-block'
import PreorderBackorderLabel from '../preorder-backorder-label'
import {getOrderAvailabilityStatus} from '../../utils/order-availability-utils'

const ProductViewHeader = ({category, isCategoryLoading}) => {
    const style = useMultiStyleConfig('ProductView')

    return (
        <>
            {isCategoryLoading || category?.length === 0 ? (
                <Box>
                    <Skeleton
                        my={{base: remCalc(20), md: remCalc(16)}}
                        h={{base: remCalc(32), md: remCalc(24)}}
                        w={{base: '100%', md: remCalc(350)}}
                    />
                </Box>
            ) : (
                <Box {...style.breadcrumbContainer}>
                    <Breadcrumb categories={category} />
                </Box>
            )}
        </>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    shortDescription: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    addToWishlist: PropTypes.func,
    pdpPlusData: PropTypes.shape(),
    updateWishlist: PropTypes.func,
    isWishlistLoading: PropTypes.bool,
    isProductPartOfSet: PropTypes.bool,
    isCategoryLoading: PropTypes.bool,
}

//Context to avoid prop drilling the images alt text
export const ProductImageAltTextContext = React.createContext()

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product,
            category,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            isCategoryLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            // setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            isCorporate,
            pdpPlusData,
            isBraun = false,
            onFocusLinkClick,
        },
        ref,
    ) => {
        const imageGalleryStyles = useMultiStyleConfig('ImageGallery')
        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const intl = useIntl()
        const location = useLocation()
        const {locale} = useMultiSite()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose,
        } = useAddToCartModalContext()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false)
        const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
        const {data: basket} = useCurrentBasket()
        const {addLayer} = useGoogleTagManager()
        const {currency} = useCurrency()
        const InfoStockIcon = BRAND_KEY === 'braun' ? InfoLabelIcon : InfoIcon
        const OutOfStockIcon = BRAND_KEY === 'braun' ? CloseLabelIcon : CloseIcon
        const OutOfStockVariant = BRAND_KEY === 'braun' ? 'braun-out-of-stock' : 'outOfStock'
        const LastFewItemsVariant = BRAND_KEY === 'braun' ? 'braun-last-few-items' : 'lastFewItems'
        const {isCorporateSale, corporateSaleLabelName} = useCorporateSale(product)
        const {isQuantityAllowed, getMaxQuantityErrorMessage, getMaxQuantity} =
            useMaxQuantityItems()

        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            // minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            // stepQuantity,
            isOutOfStock,
            unfulfillable,
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

        const priceData = useMemo(() => {
            return getPriceData(product, {quantity})
        }, [product, quantity])

        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)
        const style = useMultiStyleConfig('ProductView')
        const handleWishlistItem = async () => {
            if (!updateWishlist && !addToWishlist) return null
            if (updateWishlist) {
                updateWishlist(product, variant, quantity)
                return
            }
            addToWishlist(product, variant, quantity)
        }
        const [isModalOpen, setIsModalOpen] = useState(false)
        const buttonRef = useRef(null)
        const modalAddToCart = useRef(isAddToCartModalOpen)

        const {customInventoryMessage, shouldShowInventoryMessage} = useMemo(() => {
            // Check if product is available for preorder or backorder
            const orderStatus = getOrderAvailabilityStatus(product)
            const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

            // If product is preorder or backorder, don't show any inventory messages
            let shouldDisableButton = isPreorderOrBackorder ? false : showInventoryMessage
            let shouldShowInventory = isPreorderOrBackorder ? false : showInventoryMessage
            let currentInventoryMsg = ''

            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    },
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName,
                            },
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.OUT_OF_STOCK_FOR_PRODUCT,
                            {productName: unavailableChildProduct.productName},
                        )
                    }
                }
            }
            return {
                disableButton: shouldDisableButton,
                customInventoryMessage: currentInventoryMsg,
                shouldShowInventoryMessage: shouldShowInventory,
            }
        }, [showInventoryMessage, childProductOrderability, product])

        // Check if product is preorder/backorder to allow button even if not orderable
        const orderStatus = getOrderAvailabilityStatus(product)
        const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder
        const isBackorderCase = orderStatus.isBackorder && !orderStatus.isPreorder
        const isOutOfStockCase =
            product?.inventory?.stockLevel === 0 &&
            !orderStatus.isPreorder &&
            !orderStatus.isBackorder
        const shouldShowNotifyMe = isBackorderCase || isOutOfStockCase
        const buttonDisabled =
            !isPreorderOrBackorder && !product?.inventory?.orderable && !product?.c_isPDPWhereToBuy
        const isVariationGroup =
            product?.hitType === 'variation_group' || !!product?.type?.variationGroup

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts

            // Skip validation for preorder/backorder products
            const orderStatus = getOrderAvailabilityStatus(product)
            const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

            if (isPreorderOrBackorder) {
                toggleShowOptionsMessage(false)
                return true
            }

            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }

            return hasValidSelection
        }

        useEffect(() => {
            if (modalAddToCart.current === true && isAddToCartModalOpen === false) {
                requestAnimationFrame(() => {
                    buttonRef.current?.focus()
                })
            }
            modalAddToCart.current = isAddToCartModalOpen
        }, [isAddToCartModalOpen])

        const renderActionButtons = () => {
            if (!product) {
                return []
            }
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update',
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'global.add-to-cart',
                }),
                preorderNow: intl.formatMessage({
                    defaultMessage: 'Preorder now',
                    id: 'global.preorder_now',
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart',
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart',
                }),
                whereToBuy: intl.formatMessage({
                    defaultMessage: 'Where to buy',
                    id: 'product_view.button.where_to_buy',
                }),
            }

            const addToCartEventPDP = async () => {
                await addLayer(polish_ecommerce_gtm_event.bind(this))
                await addLayer(
                    item_gtm_event.bind(this, {
                        event: 'add_to_cart',
                        currency: currency,
                        bundle: 'false',
                        items: [
                            {
                                item_name: product?.c_defaultName,
                                item_id: product?.id,
                                item_category: product?.c_defaultCategoryName,
                                item_variant: product?.c_subModelIdentifier,
                                price: parseFloat(
                                    (
                                        product?.price /
                                        (1 + parseFloat(product?.c_taxRate) / 100)
                                    ).toFixed(2),
                                ),
                                fullprice: getPriceData(product)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '',
                                // get_reevoo_rating() || get_bv_rating(),
                                erp_category: product?.c_materialGroup,
                                prdha_category: product?.c_prdha_category,
                                quantity: quantity,
                            },
                        ],
                    }),
                )
            }
            const handleCartItem = async () => {
                if (!isQuantityAllowed(product, quantity)) {
                    showToast({
                        title: getMaxQuantityErrorMessage(getMaxQuantity(product)),
                        status: 'error',
                    })
                    setIsLoadingAddToCart(false)
                    return
                }
                // check if there is already a customized product in the cart
                if (basket?.productItems?.[0]?.c_officinaConfigurationId) {
                    return showToast({
                        title: intl.formatMessage({
                            defaultMessage:
                                'Since you are adding a customized product to your cart, your order cannot include other types of products. Before adding other products to your cart, complete your order.',
                            id: 'officina.error.complete_your_order',
                        }),
                        status: 'error',
                    })
                }

                setIsLoadingAddToCart(true)
                const hasValidSelection = validateAndShowError()

                if (!hasValidSelection) {
                    setIsLoadingAddToCart(false)
                    return null
                }

                if (!addToCart && !updateCart) {
                    setIsLoadingAddToCart(false)
                    return null
                }
                if (updateCart) {
                    await updateCart(variant || product, quantity)
                    setIsLoadingAddToCart(false)
                    return
                }

                // --- Check if basket already has all stock ---
                const orderStatus = getOrderAvailabilityStatus(product)
                const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

                // Skip stock check for preorder/backorder as they use custom preference limits
                if (!isPreorderOrBackorder) {
                    const basketQuantity =
                        basket?.productItems
                            ?.filter(
                                (item) => item.productId === (variant?.productId || product?.id),
                            )
                            ?.reduce((sum, item) => sum + item.quantity, 0) || 0
                    const availableStock = stockLevel ?? product?.inventory?.stockLevel ?? 0

                    if (basketQuantity >= availableStock) {
                        showToast({
                            title: intl.formatMessage(
                                ADD_TO_CART_ERROR_MESSAGES.QUANTITY_EXCEEDS_STOCK,
                            ),
                            status: 'error',
                        })
                        setIsLoadingAddToCart(false)
                        return
                    }
                }
                // --- END stock check ---

                try {
                    const itemsAdded = await addToCart(variant, quantity)
                    if (itemsAdded) {
                        onAddToCartModalOpen({
                            product,
                            itemsAdded,
                            selectedQuantity: quantity,
                        })
                    }
                } catch (e) {
                    showToast({
                        title: intl.formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                    })
                }
                setIsLoadingAddToCart(false)
                addToCartEventPDP()
            }

            const handleButtonClick = (e) => {
                buttonRef.current = e.currentTarget
                if (product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy) {
                    setIsModalOpen(true)
                } else {
                    handleCartItem()
                }
            }

            const handleCloseModal = () => {
                setIsModalOpen(false)
            }

            // child product of bundles do not have add to cart button
            if (
                (addToCart || updateCart) &&
                !isProductPartOfBundle &&
                !buttonDisabled &&
                !notShoppableCountry &&
                !isVariationGroup
            ) {
                // Check if product is in preorder status
                const orderStatus = getOrderAvailabilityStatus(product)
                buttons.push(
                    <Button
                        ref={buttonRef}
                        className="js-cart-add"
                        data-product-code={product.id}
                        key="cart-button"
                        onClick={handleButtonClick}
                        isDisabled={buttonDisabled}
                        isLoading={!isServer && (isBasketLoading || isLoadingAddToCart)}
                        width="100%"
                        variant="primary"
                        suppressHydrationWarning
                    >
                        {product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy
                            ? buttonText.whereToBuy
                            : updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : orderStatus.isPreorder
                            ? buttonText.preorderNow
                            : buttonText.addToCart}
                    </Button>,
                    <WhereToBuyModal
                        key="where-to-buy-modal"
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                    />,
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({
                validateOrderability: validateAndShowError,
                renderActionButtons: renderActionButtons,
            })
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName:
                            product?.c_variantIdentifier || product?.name || product?.productName,
                    },
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const lastItemThreshold = useCustomPreference('showLastItemThreshold')
        const digiohVendorID = useCustomPreference('digiohVendorID')
        const subscriptionRedirectPDP = useCustomPreferenceWithFallback('subscriptionRedirectPDP')

        const subscribeButton = product?.c_recommendations?.find(
            (rec) => rec?.recommendationType?.value === 4,
        ) &&
            subscriptionRedirectPDP && (
                <Button
                    w="100%"
                    mt={remCalc(8)}
                    variant="secondary"
                    as={Link}
                    to={subscriptionRedirectPDP}
                    // onClick={() =>
                    //     onAddToCartModalOpen({
                    //         itemsAdded: [{product, quantity: 1}],
                    //         isSubscription: true,
                    //     })
                    // }
                >
                    <FormattedMessage
                        defaultMessage="Buy in subscription"
                        id="product_item.subscription.button.buy_subscription"
                    />
                </Button>
            )

        const productImageAltText = `${product?.c_categoryName}, ${product?.c_variantIdentifier}, ${product?.variationValues?.skuIdentifier}, ${product?.c_marketingColour}`

        const powerImages =
            typeof product?.c_localizedPowerImages === 'string'
                ? parseAndSanitizeJSONWithSpaces(product?.c_localizedPowerImages)
                : Array.isArray(product?.c_localizedPowerImages)
                ? product?.c_localizedPowerImages
                : []
        const productBannerInfo = product?.c_bannerInfo

        return (
            <Flex {...style.flexContainer} data-testid="product-view" ref={ref}>
                <ProductViewHeader category={category} isCategoryLoading={isCategoryLoading} />
                <Flex {...style.productContainer}>
                    {showImageGallery && (
                        <Box {...style.imageGalleryBox}>
                            {product && !isProductLoading ? (
                                <>
                                    <Flex {...imageGalleryStyles.infoLabelContainer}>
                                        <InformativeLabelGroup
                                            product={product}
                                            priceData={priceData}
                                            isCorporateSale={isCorporateSale}
                                            corporateSaleLabelName={corporateSaleLabelName}
                                        />
                                    </Flex>
                                    <ProductImageAltTextContext.Provider
                                        value={productImageAltText}
                                    >
                                        <ImageGallery
                                            key={product.id}
                                            size={imageSize}
                                            imageGroups={product.imageGroups}
                                            powerImages={powerImages}
                                            selectedVariationAttributes={variationParams}
                                            lazy={isProductPartOfSet || isProductPartOfBundle}
                                            videoUrl={product?.c_youtubeVideoGeneric}
                                            subModelIdentifier={product?.c_subModelIdentifier}
                                        />
                                    </ProductImageAltTextContext.Provider>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}
                    <Divider {...style.horizontalDivider} />
                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack {...style.productCard} data-ab="right-column">
                        <Box {...style.productHeaderContainer}>
                            {isProductLoading ? (
                                <Skeleton h={remCalc(111)} w="100%" />
                            ) : (
                                <ProductHeader
                                    name={
                                        product?.c_variantIdentifier ||
                                        product?.name ||
                                        product?.productName
                                    }
                                    product={product}
                                    handleWishlistItem={handleWishlistItem}
                                    isWishlistLoading={isWishlistLoading}
                                />
                            )}
                        </Box>
                        {!isProductLoading && variationAttributes.length ? (
                            <VStack align="stretch" gap={0} w="full">
                                {isProductPartOfBundle && (
                                    <Box>
                                        <Text fontWeight="medium" fontSize="md" aria-label="price">
                                            <label>
                                                {intl.formatMessage({
                                                    defaultMessage: 'Quantity',
                                                    id: 'product_view.label.quantity',
                                                })}
                                                : {childOfBundleQuantity}
                                            </label>
                                        </Text>
                                    </Box>
                                )}
                                {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                                {isProductLoading ? (
                                    <VStack gap={remCalc(16)}>
                                        <Skeleton height={remCalc(24)} width="100%" />
                                        <Flex gap={remCalc(8)}>
                                            <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                            <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                            <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                            <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                        </Flex>
                                    </VStack>
                                ) : (
                                    <ProductVariants
                                        variationAttributes={variationAttributes}
                                        product={product}
                                        lastItemThreshold={lastItemThreshold}
                                        InfoStockIcon={InfoStockIcon}
                                        OutOfStockIcon={OutOfStockIcon}
                                        LastFewItemsVariant={LastFewItemsVariant}
                                        OutOfStockVariant={OutOfStockVariant}
                                        priceData={priceData}
                                        activeCurrency={activeCurrency}
                                    />
                                )}
                                <Box ref={errorContainerRef}>
                                    {!showLoading && showOptionsMessage && (
                                        <Fade in={true}>
                                            <Text
                                                color="text.danger"
                                                fontWeight={'semibold'}
                                                marginBottom={8}
                                            >
                                                {intl.formatMessage(
                                                    ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                                )}
                                            </Text>
                                        </Fade>
                                    )}
                                </Box>
                                {isProductASet && <p>{product?.shortDescription}</p>}
                                {!showLoading &&
                                    !orderStatus.isPreorder &&
                                    !orderStatus.isBackorder &&
                                    product?.inventory?.orderable &&
                                    product.inventory.stockLevel <= lastItemThreshold && (
                                        <Box {...style.inventoryLabelContainer}>
                                            <Fade in>
                                                <StockLabel
                                                    variant={LastFewItemsVariant}
                                                    icon={
                                                        <InfoStockIcon circleColor="transparent" />
                                                    }
                                                    textMessage={
                                                        <FormattedMessage
                                                            id="use_product.message.low_stock"
                                                            defaultMessage="Last {quantity} {itemLabel}"
                                                            values={{
                                                                quantity:
                                                                    product?.inventory?.stockLevel,
                                                                itemLabel:
                                                                    product?.inventory
                                                                        ?.stockLevel === 1 ? (
                                                                        <FormattedMessage
                                                                            id="global.message.item"
                                                                            defaultMessage="item"
                                                                        />
                                                                    ) : (
                                                                        <FormattedMessage
                                                                            id="global.message.items"
                                                                            defaultMessage="items"
                                                                        />
                                                                    ),
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Fade>
                                        </Box>
                                    )}

                                {!showLoading &&
                                    shouldShowInventoryMessage &&
                                    !product?.inventory?.orderable &&
                                    !product?.c_isPDPWhereToBuy && (
                                        <Box {...style.inventoryLabelContainer}>
                                            <Fade in>
                                                <StockLabel
                                                    variant={OutOfStockVariant}
                                                    icon={<OutOfStockIcon />}
                                                    textMessage={
                                                        <FormattedMessage
                                                            id="use_product.message.out_of_stock"
                                                            defaultMessage="Out of stock"
                                                        />
                                                    }
                                                />
                                            </Fade>
                                        </Box>
                                    )}
                            </VStack>
                        ) : (
                            isProductLoading && (
                                <VStack gap={remCalc(16)}>
                                    <Skeleton height={remCalc(24)} width="100%" />
                                    <Flex gap={remCalc(8)}>
                                        <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                        <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                        <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                        <Skeleton height={remCalc(92)} width={remCalc(92)} />
                                    </Flex>
                                </VStack>
                            )
                        )}
                        <Divider {...style.horizontalDivider} />

                        <Box {...style.priceBox}>
                            <Box {...style.priceAndKlarnaBox}>
                                {!isBraun && (
                                    <>
                                        {isProductLoading ? (
                                            <Skeleton h={remCalc(76)} mb={remCalc(16)} w="100%" />
                                        ) : (
                                            <PriceCard
                                                priceData={priceData}
                                                product={product}
                                                activeCurrency={activeCurrency}
                                            />
                                        )}
                                    </>
                                )}
                                <Box w="100%">
                                    {!isProductLoading && priceData?.currentPrice ? (
                                        <>
                                            <KlarnaWidget price={priceData} />
                                            <PaypalWidget
                                                price={priceData?.currentPrice}
                                                placement="product"
                                            />
                                        </>
                                    ) : isProductLoading ? (
                                        <Skeleton h={remCalc(59)} w="100%" />
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                            </Box>
                            {product?.c_energyEfficiencyClass && (
                                <Box {...style.energyLabel}>
                                    <EnergyLabel
                                        level={product.c_energyEfficiencyClass}
                                        description={intl.formatMessage({
                                            defaultMessage: 'Product information',
                                            id: 'energy_label.product_information',
                                        })}
                                        manualDocument={product?.c_manualDocument}
                                        textWidth={'big'}
                                        labelPosition="top"
                                    />
                                </Box>
                            )}
                            {!showLoading && customInventoryMessage && (
                                <Fade in={true}>
                                    <Text
                                        color="orange.600"
                                        fontWeight={'semibold'}
                                        marginBottom={8}
                                    >
                                        {customInventoryMessage}
                                    </Text>
                                </Fade>
                            )}

                            <Box minHeight={remCalc(40)} data-testid="button-container-pdp">
                                {/* Preorder/Backorder Labels */}
                                {!showLoading &&
                                    product &&
                                    !orderStatus.isBackorder &&
                                    orderStatus.isPreorder && (
                                        <PreorderBackorderLabel
                                            product={product}
                                            locale={locale.id}
                                            variant="preorder"
                                        />
                                    )}
                                {!showLoading &&
                                    product &&
                                    !orderStatus.isPreorder &&
                                    orderStatus.isBackorder && (
                                        <PreorderBackorderLabel
                                            product={product}
                                            locale={locale.id}
                                            variant="backorder"
                                        />
                                    )}
                                {(showLoading || product?.c_skuIsPDPOnly !== 'true') && (
                                    <Box
                                        gap={remCalc(8)}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems={'stretch'}
                                        width={'100%'}
                                    >
                                        <Box
                                            display={
                                                buttonDisabled
                                                    ? 'none'
                                                    : isProductPartOfSet
                                                    ? 'block'
                                                    : ['none', 'none', 'none', 'block']
                                            }
                                        >
                                            {renderActionButtons()}
                                            {subscribeButton}
                                        </Box>
                                        <Box
                                            display={
                                                buttonDisabled
                                                    ? 'none'
                                                    : isProductPartOfSet || isProductPartOfBundle
                                                    ? 'none'
                                                    : ['block', 'block', 'block', 'none']
                                            }
                                        >
                                            {renderActionButtons()}
                                            {subscribeButton}
                                        </Box>
                                        {/* Show Notify Me button for backorder or out of stock (but not preorder) */}
                                        {shouldShowNotifyMe && (
                                            <NotifyMeButton
                                                product={product}
                                                isBackorder={isBackorderCase}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Box>
                                {!isCorporate &&
                                    pdpPlusData?.column_editorial_components?.map((component) => (
                                        <EditorialComponentsFactory
                                            key={component.uid}
                                            componentType={component?._content_type_uid}
                                            {...component}
                                        />
                                    ))}
                                <Flex {...style.benefitsAndBannerBox}>
                                    <Box id="mcp-pdp-container"></Box>
                                    {digiohVendorID && <div id="digioh-pdp-container"></div>}
                                    <Benefits />
                                    {isProductLoading ? (
                                        <Skeleton h={remCalc(158)} w="100%" />
                                    ) : (
                                        <ProductDetailBannerBlock
                                            productBannerInfo={productBannerInfo}
                                            onFocusLinkClick={onFocusLinkClick}
                                        />
                                    )}
                                </Flex>
                            </Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    },
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isCategoryLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    isCorporate: PropTypes.bool,
    pdpPlusData: PropTypes.shape(),
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    isBraun: PropTypes.bool,
    onFocusLinkClick: PropTypes.func,
}

export default ProductView
